import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityToolbar } from './components/AccessibilityToolbar';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { TenantAdminDashboard } from './components/TenantAdminDashboard';
import { DownloadAppsPage } from './components/DownloadAppsPage';
import { AACBoard } from './components/AACBoard';
import { ExecutiveDashboard } from './components/ExecutiveDashboard';
import { SaaSLandingPage } from './components/SaaSLandingPage';
import { AuthModal } from './components/AuthModal';
import { ModuleExplorer } from './components/ModuleExplorer';
import { 
  Shield, Volume2, TrendingUp, LogIn, LogOut, User, Globe, Sparkles, Grid, Users, Smartphone, Download
} from 'lucide-react';

export interface UserSession {
  name: string;
  email: string;
  role: string;
  tenant: string;
}

export const AppShell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'landing' | 'admin' | 'school_admin' | 'downloads' | 'aac' | 'analytics' | 'modules'>('landing');
  const [activeModuleId, setActiveModuleId] = useState('aac');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserSession | null>(null);

  const handleLoginSuccess = (user: UserSession) => {
    setCurrentUser(user);
    if (user.role === 'SUPER_ADMIN') {
      setActiveTab('admin');
    } else {
      setActiveTab('school_admin');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setActiveTab('landing');
  };

  const handleLaunchModule = (moduleId: string) => {
    setActiveModuleId(moduleId);
    setActiveTab('modules');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      
      {/* Top Application Navigation Header */}
      <header className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white shadow-lg px-6 py-3.5 flex items-center justify-between sticky top-0 z-40">
        
        {/* Brand Logo & Name */}
        <div 
          onClick={() => setActiveTab('landing')}
          className="flex items-center gap-3 cursor-pointer group"
        >
          <div className="w-10 h-10 bg-white text-blue-800 rounded-xl flex items-center justify-center font-black text-xl shadow-md group-hover:scale-105 transition">
            A
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-extrabold leading-none tracking-tight">{t('app_name')}</h1>
              <span className="px-2 py-0.5 bg-blue-500/30 text-blue-200 border border-blue-400/30 text-[10px] font-bold rounded-full uppercase tracking-wider">
                Enterprise SaaS
              </span>
            </div>
            <p className="text-xs text-blue-200 mt-0.5">{t('tagline')}</p>
          </div>
        </div>

        {/* Global Navigation Tabs */}
        <nav className="hidden md:flex items-center gap-1.5 bg-blue-950/40 p-1.5 rounded-xl border border-white/10 backdrop-blur-md">
          <button
            onClick={() => setActiveTab('landing')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'landing' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            SaaS Overview
          </button>

          <button
            onClick={() => setActiveTab('modules')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'modules' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Grid className="w-3.5 h-3.5 text-amber-400" />
            16-Module Suite
          </button>

          <button
            onClick={() => setActiveTab('downloads')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'downloads' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
            Apps & Downloads
          </button>

          <button
            onClick={() => setActiveTab('admin')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'admin' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            Super Admin
          </button>

          <button
            onClick={() => setActiveTab('school_admin')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'school_admin' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Users className="w-3.5 h-3.5 text-emerald-400" />
            School Admin (Students / Excel)
          </button>

          <button
            onClick={() => setActiveTab('aac')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'aac' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Volume2 className="w-3.5 h-3.5" />
            AAC Speech Board
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'analytics' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Analytics
          </button>
        </nav>

        {/* Language Switcher & User Session / Auth Controls */}
        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <div className="flex items-center bg-blue-950/60 p-1 rounded-xl border border-white/10 text-xs">
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`px-2 py-0.5 rounded font-bold transition ${i18n.language === 'en' ? 'bg-white text-blue-900' : 'text-blue-200 hover:text-white'}`}
            >
              EN
            </button>
            <button
              onClick={() => i18n.changeLanguage('hi')}
              className={`px-2 py-0.5 rounded font-bold transition ${i18n.language === 'hi' ? 'bg-white text-blue-900' : 'text-blue-200 hover:text-white'}`}
            >
              HI
            </button>
            <button
              onClick={() => i18n.changeLanguage('mr')}
              className={`px-2 py-0.5 rounded font-bold transition ${i18n.language === 'mr' ? 'bg-white text-blue-900' : 'text-blue-200 hover:text-white'}`}
            >
              MR
            </button>
          </div>

          {currentUser ? (
            <div className="flex items-center gap-3 bg-white/10 border border-white/20 pl-3 pr-1.5 py-1.5 rounded-xl backdrop-blur-md">
              <div className="text-right">
                <div className="text-xs font-bold text-white flex items-center gap-1 justify-end">
                  <span>{currentUser.name}</span>
                  <span className="px-1.5 py-0.2 bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 text-[9px] font-extrabold rounded">
                    {currentUser.role}
                  </span>
                </div>
                <div className="text-[10px] text-blue-200">{currentUser.tenant}</div>
              </div>
              <button
                onClick={handleLogout}
                title="Sign Out"
                className="p-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-200 border border-red-400/30 rounded-lg transition"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAuthModalOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl text-xs shadow-md transition flex items-center gap-2"
            >
              <LogIn className="w-4 h-4" />
              <span>Sign In / Demo Login</span>
            </button>
          )}
        </div>

      </header>

      {/* Main Workspace Router */}
      <main id="main-content" className="flex-1 w-full">
        {activeTab === 'landing' && (
          <SaaSLandingPage 
            onOpenAuth={() => setIsAuthModalOpen(true)}
            onExploreDemo={(tab) => {
              if (tab === 'admin' || tab === 'aac' || tab === 'analytics') {
                setActiveTab(tab);
              } else {
                handleLaunchModule(tab);
              }
            }}
            onSelectModule={handleLaunchModule}
          />
        )}

        {activeTab === 'modules' && (
          <div className="p-6 max-w-7xl w-full mx-auto">
            <ModuleExplorer initialModule={activeModuleId} />
          </div>
        )}

        {activeTab === 'downloads' && (
          <div className="p-6 max-w-7xl w-full mx-auto">
            <DownloadAppsPage />
          </div>
        )}
        
        {activeTab === 'admin' && (
          <div className="p-6 max-w-7xl w-full mx-auto">
            <SuperAdminDashboard />
          </div>
        )}

        {activeTab === 'school_admin' && (
          <div className="p-6 max-w-7xl w-full mx-auto">
            <TenantAdminDashboard />
          </div>
        )}

        {activeTab === 'aac' && (
          <div className="p-6 max-w-7xl w-full mx-auto">
            <AACBoard />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="p-6 max-w-7xl w-full mx-auto">
            <ExecutiveDashboard />
          </div>
        )}
      </main>

      {/* Floating Accessibility Control Toolbar */}
      <AccessibilityToolbar />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};
